import { useId, type ChangeEvent, type ReactNode } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import NativeSelect from "@mui/material/NativeSelect";
import OutlinedInput from "@mui/material/OutlinedInput";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import type { LucideIcon } from "lucide-react";
import { WELL } from "@/base/well";

// The building blocks a BaseForm page composes: section cards, stat tiles,
// honest placeholders and labelled fields. All MUI, all in the brk palette.

/** A titled white card inside a tab (e.g. "Business information"). Its title
 *  is an h2 unless the page has its own: a wizard step's cards sit under the
 *  step's h2, so they title in h3 (`titleAs`, BRK-S349). An `icon` sits in a
 *  tinted tile before the title, as a wizard step's own heading shows it
 *  (BRK-S356); it is decorative, so the card's name stays its title. */
export function SectionCard({
  title,
  titleAs = "h2",
  icon: Icon,
  action,
  children,
}: {
  title?: string;
  titleAs?: "h2" | "h3";
  icon?: LucideIcon;
  action?: ReactNode;
  children: ReactNode;
}) {
  const headingId = useId();
  return (
    <Box
      component="section"
      aria-labelledby={title ? headingId : undefined}
      // The shared well (BRK-S502), the one CollapsibleBox draws too.
      className={WELL}
    >
      {(title || action) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1,
          }}
        >
          {title && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                minWidth: 0,
              }}
            >
              {Icon && (
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                    color: "primary.main",
                  }}
                >
                  <Icon size={16} />
                </Box>
              )}
              <Typography id={headingId} component={titleAs} variant="h2">
                {title}
              </Typography>
            </Box>
          )}
          {action}
        </Box>
      )}
      {children}
    </Box>
  );
}

/** A record's avatar tile beside its title: its initials on the accent,
 *  40px. Decorative, since the title names the record: the account record's
 *  (through BaseForm) and the submission's page's (BRK-S358). */
export function RecordAvatar({ text }: { text: string }) {
  return (
    <Avatar
      variant="rounded"
      aria-hidden
      sx={{
        width: 40,
        height: 40,
        bgcolor: "primary.main",
        typography: "h3",
      }}
    >
      {text}
    </Avatar>
  );
}

/** A sub-heading inside a SectionCard, with the spacing the design uses. */
export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Typography component="h3" variant="h3" sx={{ mt: 2.5, mb: 1.5 }}>
      {children}
    </Typography>
  );
}

/** A responsive grid of fields: one column on a phone, more as width allows. */
export function FieldGrid({
  children,
  min = 240,
}: {
  children: ReactNode;
  min?: number;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
        gap: "12px 16px",
      }}
    >
      {children}
    </Box>
  );
}

/** A record's summary figures, one strip of label-over-value pairs
 *  (BRK-S263). Six sit in one row from xl, three from sm and two on a phone.
 *  A value the system cannot know yet renders as "—", and `note` says why
 *  once for the strip, never an invented number. */
export function StatStrip({
  label,
  stats,
  note,
}: {
  /** The list's accessible name. */
  label: string;
  stats: readonly { label: string; value: ReactNode }[];
  note?: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", xl: "row" },
        alignItems: { xl: "center" },
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Box
        role="list"
        aria-label={label}
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            xl: `repeat(${stats.length}, minmax(0, 1fr))`,
          },
        }}
      >
        {stats.map((stat) => (
          <Box role="listitem" key={stat.label} sx={{ px: 2, py: 1 }}>
            <Typography
              variant="caption"
              component="div"
              sx={{ color: "text.secondary" }}
            >
              {stat.label}
            </Typography>
            <Typography
              variant="h3"
              component="div"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>
      {note && (
        <Typography
          variant="caption"
          component="p"
          sx={{
            color: "text.secondary",
            px: 2,
            pb: { xs: 1, xl: 0 },
            maxWidth: { xl: "14rem" },
          }}
        >
          {note}
        </Typography>
      )}
    </Box>
  );
}

/** A dashed, clearly-a-placeholder panel for something not built yet. Its
 *  title is a heading where the panel is a section of the page (`titleAs`). */
export function PlaceholderPanel({
  title,
  titleAs = "p",
  children,
  action,
}: {
  title?: string;
  titleAs?: "p" | "h2" | "h3";
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 2.5,
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 220 }}>
        {title && (
          <Typography variant="h3" component={titleAs}>
            {title}
          </Typography>
        )}
        {children && (
          <Typography
            variant="body2"
            component="div"
            sx={{ color: "text.secondary", mt: title ? 0.25 : 0 }}
          >
            {children}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

/** A field's label sits above it, in the theme's label style: meta at 500,
 *  sentence case (BRK-S263; it was 11px uppercase). */
const labelSx = {
  display: "block",
  color: "text.primary",
  mb: 0.5,
  "&.Mui-focused": { color: "text.primary" },
} as const;

interface FieldBase {
  label: string;
  helperText?: string;
  error?: string;
  readOnly?: boolean;
  /** Span every column of a FieldGrid. */
  full?: boolean;
}

/** A labelled text input. The label sits above the field (the base design),
 *  and is the input's accessible name exactly — no asterisk inside it. */
export function TextInputField({
  label,
  value,
  onChange,
  helperText,
  error,
  readOnly,
  full,
  type = "text",
  inputMode,
  multiline,
  rows,
  autoComplete = "off",
  placeholder,
}: FieldBase & {
  value: string;
  onChange: (value: string) => void;
  type?: string;
  inputMode?: "text" | "numeric" | "decimal" | "email" | "tel" | "url";
  multiline?: boolean;
  rows?: number;
  autoComplete?: string;
  placeholder?: string;
}) {
  const id = useId();
  const helpId = `${id}-help`;
  return (
    <FormControl
      fullWidth
      error={Boolean(error)}
      sx={full ? { gridColumn: "1 / -1" } : undefined}
    >
      <FormLabel htmlFor={id} sx={labelSx}>
        {label}
      </FormLabel>
      <OutlinedInput
        id={id}
        value={value}
        type={type}
        size="small"
        multiline={multiline}
        minRows={multiline ? rows : undefined}
        readOnly={readOnly}
        placeholder={placeholder}
        inputProps={{
          inputMode,
          autoComplete,
          "aria-describedby": error || helperText ? helpId : undefined,
        }}
        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        sx={{ bgcolor: readOnly ? "action.hover" : "background.paper" }}
      />
      {(error || helperText) && (
        <FormHelperText id={helpId}>{error ?? helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

/** A labelled native select: known value sets, keyboard- and test-friendly. */
export function SelectInputField({
  label,
  value,
  onChange,
  options,
  placeholder,
  helperText,
  error,
  readOnly,
  full,
}: FieldBase & {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  const id = useId();
  return (
    <FormControl
      fullWidth
      error={Boolean(error)}
      sx={full ? { gridColumn: "1 / -1" } : undefined}
    >
      <FormLabel htmlFor={id} sx={labelSx}>
        {label}
      </FormLabel>
      <NativeSelect
        id={id}
        value={value}
        disabled={readOnly}
        input={<OutlinedInput size="small" />}
        onChange={(e) => onChange(e.target.value)}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </NativeSelect>
      {(error || helperText) && (
        <FormHelperText>{error ?? helperText}</FormHelperText>
      )}
    </FormControl>
  );
}
